import { useState } from 'react'
import Button from '../Button'
import { PersonDto } from '../../api/person/models/PersonDto'
import { SetExtra } from '../../utils/SetExtra'
import LoadingSpinner from '../LoadingSpinner'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faCheckCircle,
    faMagnifyingGlass,
} from '@fortawesome/free-solid-svg-icons'
import { toast, Toaster } from 'sonner'
import Section from '../Section'
import PersonUi from '../PersonUi'
import { parse } from 'lucene-query-parser'
import personApi from '../../api/person/PersonApi'
import { PersonAndScore } from '../../api/person/models/getCandidatesBooleanSearch/CandidatesBooleanSearch'
import { formatText, getCountryFlag } from '../../utils/personUtils'
import JobTimeline from '../JobTimeline'
import ToggleSwitch from '../ToggleSwitch'
import ExpandableText from '../ExpandableText'
import FieldSearchEditor from './FieldSearchEditor'
import BooleanSearchInstructions from './BooleanSearchInstructions'

export default function BooleanSearch(): React.JSX.Element {
    const DEFAULT_QUERY =
        'headline:"software engineer" OR cloud^4 OR skills:(AWS OR GCP OR cloud)'
    const [query, setQuery] = useState(DEFAULT_QUERY)
    const [validationMessage, setValidationMessage] = useState<string | null>(
        null,
    )
    const [sidebarVisible, setSidebarVisible] = useState(true)

    // const [personLites, setPersonLites] = useState<PersonLiteDto[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [results, setResults] = useState<PersonAndScore[]>([])
    const [persons, setPersons] = useState<PersonDto[]>([])
    const [ignoreRetiredPeople, setIgnoreRetiredPeople] = useState(true)
    // determine platform once
    function isMacOS(): boolean {
        const uaData = (navigator as any).userAgentData
        if (uaData?.platform) {
            return uaData.platform.toLowerCase().includes('mac')
        }
        // fallback if userAgentData isn’t supported
        return /Mac/i.test(navigator.userAgent)
    }

    const isMac = isMacOS()

    const validateSyntax = () => {
        try {
            parse(query)
            setValidationMessage('✅ Query syntax is valid.')
        } catch (e: any) {
            setValidationMessage(`❌ Invalid query: ${e.message}`)
        }
    }

    const handleIgnoreRetiredPeopleChange = (checked: boolean) => {
        setIgnoreRetiredPeople(checked)
    }
    async function search(valueToSearch?: string) {
        setIsLoading(true)
        setResults([])
        // setPersonLites([])
        setPersons([])
        try {
            const queryToSearch = valueToSearch ?? query

            // return the expression tree
            parse(queryToSearch) // ✅ Validate Lucene syntax
            const candidatesIds = new SetExtra<string>()

            const res = await personApi.getCandidatesBooleanSearch(
                queryToSearch,
                ignoreRetiredPeople,
            )

            res.personsAndScores.forEach((result) => {
                candidatesIds.add(result.personId)
            })
            setResults(res.personsAndScores)

            // const getPersonsLiteRes = await personApi.getPersonsLite({
            //     personIds: candidatesIds.getValuesAsArray(),
            // })
            // setPersonLites(getPersonsLiteRes.persons)

            if (candidatesIds.getValuesAsArray().length === 0) {
                toast.error('No results found.')
                return
            }
            const resolvedPersons: PersonDto[] = (
                await personApi.getPersonsById(candidatesIds.getValuesAsArray())
            ).persons
            // const resolvedPersons: PersonDto[] = await Promise.all(
            //     candidatesIds.getValuesAsArray().map(async (candidateId) => {
            //         const result = await personApi.getPersonById(candidateId)
            //         return result.person
            //     }),
            // )

            setPersons(resolvedPersons)
        } catch (err) {
            console.error(err)
            toast.error(`Invalid search syntax: ${err}`, {
                duration: 5000,
            })
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className="w-screen relative -left-[calc((100vw-100%)/2)] px-8">
            <button
                onClick={() => setSidebarVisible(!sidebarVisible)}
                className="mb-2 p-2 border rounded bg-gray-100 hover:bg-gray-200 text-sm"
            >
                {sidebarVisible ? '<< Hide Search Help' : 'Show Search Help >>'}
            </button>
            <div className="flex">
                {sidebarVisible && (
                    <BooleanSearchInstructions
                        setQuery={setQuery}
                        setValidationMessage={setValidationMessage}
                    />
                )}
                <div className={sidebarVisible ? 'w-[70%]' : 'w-full'}>
                    <div className="font-bold my-2">
                        Automatically remove retired people?
                    </div>
                    <ToggleSwitch
                        checked={ignoreRetiredPeople === true}
                        onChange={(checked) =>
                            handleIgnoreRetiredPeopleChange(
                                checked ? true : false,
                            )
                        }
                    />
                    <div className="mt-4 font-bold">
                        <div>Boolean search query:</div>
                        <FieldSearchEditor
                            value={query}
                            onChange={(newQuery) => {
                                setQuery(newQuery)
                            }}
                            onSearch={search}
                        />
                    </div>
                    {/* <textarea
                        id="lucene-query"
                        rows={3}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        className="w-full mt-2 p-2 border rounded font-mono text-sm"
                    /> */}
                    <div className="flex gap-2">
                        <div className="flex flex-col">
                            <Button
                                onClick={() => search()}
                                type="button"
                                style="primary"
                                classes="mt-4"
                                enabled={!isLoading}
                            >
                                {isLoading && (
                                    <LoadingSpinner spinnerSize={24} />
                                )}
                                {!isLoading && (
                                    <FontAwesomeIcon icon={faMagnifyingGlass} />
                                )}
                                <div className="ml-2">Search</div>
                            </Button>
                            <div className="italic">
                                Press {isMac ? '⌘' : 'Ctrl'} + Enter to search
                            </div>
                        </div>

                        <div className="flex flex-col items-start gap-3">
                            <Button
                                onClick={validateSyntax}
                                type="button"
                                style="primary"
                                classes="mt-4"
                                enabled={!isLoading}
                            >
                                {isLoading && (
                                    <LoadingSpinner spinnerSize={24} />
                                )}
                                {!isLoading && (
                                    <FontAwesomeIcon icon={faCheckCircle} />
                                )}
                                <div className="ml-2">Validate Syntax</div>
                            </Button>

                            {validationMessage && (
                                <span
                                    className={
                                        validationMessage.includes('✅')
                                            ? 'text-green-600'
                                            : 'text-red-600'
                                    }
                                >
                                    {validationMessage}
                                </span>
                            )}
                        </div>
                    </div>

                    {results.length > 0 && persons.length > 0 ? (
                        <Section title="Search Results" className="mt-8">
                            {results.map((result) => {
                                const person = persons.find(
                                    (person) => person.id === result.personId,
                                )

                                if (!person) {
                                    throw new Error(
                                        `Person not found. Id: ${result.personId}`,
                                    )
                                }

                                return (
                                    <div
                                        key={result.personId}
                                        className="candidate-result mt-6 mb-12"
                                    >
                                        {isLoading && (
                                            <LoadingSpinner spinnerSize={24} />
                                        )}
                                        <div className="flex justify-between gap-4">
                                            <div className="w-1/6 border-r-2 p-4 bg-white border-gray-300">
                                                <PersonUi
                                                    person={person}
                                                    showCurrentJobDetails={
                                                        false
                                                    }
                                                />
                                                {person?.headline}
                                                <div className="text-gray-600 text-sm">
                                                    {getCountryFlag(
                                                        person?.location
                                                            ?.split(',')
                                                            .pop()
                                                            ?.trim() ?? '',
                                                    )}
                                                    <span className="ml-2">
                                                        {person?.location}
                                                    </span>
                                                </div>
                                                Score: {result.score}
                                            </div>
                                            <div className="w-2/6 flex flex-col pr-2">
                                                {person?.summary && (
                                                    <Section
                                                        title="Summary"
                                                        titleType="h2"
                                                        className="mt-2"
                                                    >
                                                        <ExpandableText
                                                            className="text-sm whitespace-pre-line"
                                                            text={formatText(
                                                                person.summary,
                                                            )}
                                                            maxLines={16}
                                                        />
                                                    </Section>
                                                )}
                                            </div>

                                            <div className="w-3/6">
                                                {person?.workedAts &&
                                                    person.workedAts.length >
                                                        0 && (
                                                        <div className="border-l-2 p-4 bg-white border-gray-300 mt-2 max-h-96 overflow-y-auto pr-2">
                                                            <div className="relative overflow-y-auto pr-2">
                                                                <JobTimeline
                                                                    jobs={
                                                                        person.workedAts
                                                                    }
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </Section>
                    ) : (
                        <></>
                    )}
                </div>
            </div>

            <Toaster />
        </div>
    )
}
